import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import EmailPopup from './emailpop/EmailPopup';
import GapCard from './affiliateGap/gapCard';
import { startGapPollingWithCleanup } from '../store/slices/gapBrandsSlice';
import { 
  Search, 
  Eye,
  X,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  Loader2,
  Send,
  Bookmark,
  BookmarkCheck,
  Crown,
  Lock,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Globe,
  Hash,
  MousePointer,
  Flag,
  Mail,
  Square,
  CheckSquare,
  Plus,
  Minus,
  CheckCircle
} from 'lucide-react';

// Import all functions and components from the service file
import {
  fetchGapAnalysis,
  fetchBrandData,
  getSortedData,
  formatNumber,
  getCountryFlag,
  ComparisonIndicator,
  SortIcon,
  SimpleContentBox,
  AffiliateDataTable,
  gapAnalysisEmailTemplates
} from '../services/affiliateGapTable';

// API BASE PATH
const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

// User Plan Hook
const useUserPlan = () => {
  const [userPlan, setUserPlan] = useState('free');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { 
    const fetchUserPlan = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${basePath}/api/planfetch`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Convert to lowercase to match plan configuration
        const planType = (data.plan || 'free').toLowerCase();
        
        setUserPlan(planType);
        setError(null);
      } catch (error) {
        console.error('Error fetching user plan:', error);
        setUserPlan('free');
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserPlan();
  }, []);

  return { userPlan, isLoading, error };
};

// Get plan data limits
const getPlanDataLimit = (userPlan) => {
  switch(userPlan) {
    case 'free': return 0;
    case 'growth': return 25;
    case 'pro': return 500;
    case 'enterprise': return null; // unlimited
    default: return 0;
  }
};

// Plan Status Display Component
const PlanStatusDisplay = ({ userPlan, totalResults }) => {
  const planLimit = getPlanDataLimit(userPlan);
  const planNames = {
    free: 'Free',
    growth: 'Growth',
    pro: 'Pro',
    enterprise: 'Enterprise'
  };
  
  const planName = planNames[userPlan] || 'Free';
  
  if (planLimit === null) {
    return `${totalResults} affiliates found`;
  } else if (planLimit === 0) {
    return `${totalResults} affiliates found`;
  } else {
    const dataRows = Math.min(planLimit, totalResults);
    const imageRows = Math.max(0, totalResults - planLimit);
    return `${totalResults} affiliates found `;
  }
};

const Toast = ({ message, type, isVisible, onClose }) => {
  if (!isVisible) return null;

  const getToastStyles = () => {
    switch (type) {
      case 'success':
        return 'bg-green-500 text-white border-green-600';
      case 'error':
        return 'bg-red-500 text-white border-red-600';
      case 'warning':
        return 'bg-yellow-500 text-white border-yellow-600';
      default:
        return 'bg-blue-500 text-white border-blue-600';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      default:
        return 'ℹ️';
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-right duration-300">
      <div className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border-l-4 min-w-96 ${getToastStyles()}`}>
        <span className="text-lg">{getIcon()}</span>
        <div className="flex-1">
          <p className="font-medium">{message}</p>
        </div>
        <button
          onClick={onClose}
          className="text-white hover:text-gray-200 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Geo Tooltip Component
const GeoTooltip = ({ geoBreakdown, isVisible, position }) => {
  if (!isVisible || !geoBreakdown || geoBreakdown.length === 0) {
    return null;
  }

  return (
    <div 
      className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-4 max-w-xs animate-in fade-in duration-200"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: 'translate(-50%, -100%)',
        marginTop: '-8px'
      }}
    >
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-2">
          All Geographic Traffic
        </h4>
        <div className="max-h-64 overflow-y-auto space-y-2">
          {geoBreakdown.map((geo, index) => (
            <div key={index} className="flex items-center justify-between py-1">
              <div className="flex items-center gap-2 min-w-0">
                <img 
                  src={getCountryFlag(geo.code)} 
                  alt={geo.country} 
                  className="w-5 h-3 flex-shrink-0" 
                />
                <span className="text-xs text-gray-700 truncate">{geo.country}</span>
              </div>
              <div className="text-xs font-medium text-blue-600 ml-2">
                {formatNumber(geo.total)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Expanded Row Component
const ExpandedRowContent = ({ row, pipelineStatuses = {} }) => {
  const affiliate = row.original;
  const pipelineEntries = pipelineStatuses[affiliate.affiliate_url] || [];

  const stageColorMap = {
    prospected: 'bg-blue-100 text-blue-700 border-blue-200',
    contacted: 'bg-purple-100 text-purple-700 border-purple-200',
    negotiating: 'bg-amber-100 text-amber-700 border-amber-200',
    onboarding: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    active: 'bg-green-100 text-green-700 border-green-200',
    churned: 'bg-red-100 text-red-700 border-red-200'
  };

  return (
    <td colSpan={8} className="px-6 py-6 bg-gradient-to-r from-blue-50 to-gray-50">
      <div className="max-w-full overflow-hidden">
        <div className="space-y-6">
          {/* Prospected Brands */}
          {pipelineEntries.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-gray-800 mb-3 flex items-center gap-2">
                <CheckSquare className="w-4 h-4 text-green-600" />
                Prospected Brands
              </h4>
              <div className="flex flex-wrap gap-2">
                {pipelineEntries.map((entry, idx) => (
                  <div key={idx} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${stageColorMap[entry.stage] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                    <span className="text-sm font-semibold">{entry.brand_name}</span>
                    <span className="text-[10px] uppercase font-bold opacity-75">{entry.stage}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Country Breakdown */}
          {affiliate.geoBreakdown && affiliate.geoBreakdown.length > 0 && (
            <div className="transform transition-all duration-500 hover:scale-[1.02]">
              <h4 className="text-xs font-medium text-gray-800 mb-3 flex items-center gap-2">
                <Flag className="w-4 h-4 text-blue-600" />
                Country Traffic Breakdown
              </h4>
              <div className="w-full">
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Country</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Organic Traffic</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Paid Traffic</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Total Traffic</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {affiliate.geoBreakdown.map((geo, index) => (
                          <tr key={index} className="hover:bg-blue-50 transition-colors duration-200">
                            <td className="px-4 py-3 text-xs font-medium text-gray-900 whitespace-nowrap text-center">
                              <div className="flex items-center justify-center gap-2">
                                <img 
                                  src={getCountryFlag(geo.code)} 
                                  alt={geo.country} 
                                  className="w-6 h-4 object-cover rounded"
                                />
                                {geo.country}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <div className="text-xs text-gray-900 font-medium">{formatNumber(geo.organic)}</div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <div className="text-xs text-gray-900 font-medium">{formatNumber(geo.paid)}</div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <div className="text-xs text-gray-900 font-medium">{formatNumber(geo.total)}</div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Top Pages */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
              <h4 className="text-sm font-medium text-gray-800 mb-3 flex items-center gap-2">
                <Eye className="w-4 h-4 text-blue-600" />
                Top Pages
              </h4>
              <div className="space-y-3">
                {affiliate.topPages?.map((page, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 rounded-lg hover:bg-blue-50 transition-colors">
                    <span className="text-xs font-bold text-white bg-blue-600 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="text-xs text-gray-700 font-medium">{page}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Organic Keywords */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
              <h4 className="text-sm font-medium text-gray-800 mb-3 flex items-center gap-2">
                <Hash className="w-4 h-4 text-blue-600" />
                Top Organic Keywords
              </h4>
              <div className="space-y-3">
                {affiliate.topKeywords?.map((keyword, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 rounded-lg hover:bg-blue-50 transition-colors">
                    <span className="text-xs font-bold text-white bg-blue-600 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="text-xs text-gray-700 font-medium">{keyword.keyword}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Paid Keywords */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
              <h4 className="text-sm font-medium text-gray-800 mb-3 flex items-center gap-2">
                <MousePointer className="w-4 h-4 text-blue-600" />
                Paid Keywords
              </h4>
              <div className="space-y-3">
                {affiliate.topPaidKeywords?.map((keyword, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 rounded-lg hover:bg-blue-50 transition-colors">
                    <span className="text-xs font-bold text-white bg-blue-600 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                      {index + 1}
                    </span>
                    <span className="text-xs text-gray-700 font-medium">{keyword.keyword}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </td>
  );
};

const AffiliateGapAnalyzer = () => {
  const dispatch = useDispatch();

  // Get user plan information
  const { userPlan, isLoading: planLoading, error: planError } = useUserPlan();

  // State management
  const [formData, setFormData] = useState({
    brandName: '',
    spyUrl: ''
  });
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'info'
  });
  const [emailPopup, setEmailPopup] = useState({ isOpen: false, contact: null, allEmails: [], socialMedia: {} });
  const [findingContact, setFindingContact] = useState({});
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [errors, setErrors] = useState({});
  const [urlVerified, setUrlVerified] = useState(false);
  const [verifyingUrl, setVerifyingUrl] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const verifyTimerRef = useRef(null);
  const [affiliateData, setAffiliateData] = useState([]);
  const [brands, setBrands] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [rowSelection, setRowSelection] = useState(new Set());
  const [draftAffiliates, setDraftAffiliates] = useState(new Set());
  const [showDraftOnly, setShowDraftOnly] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({
    url: true,
    totalTraffic: true,
    organicKeywords: true,
    organicTraffic: true,
    paidTraffic: true,
    topGeos: true,
    contact: true
  });
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipData, setTooltipData] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [limitExceededModal, setLimitExceededModal] = useState({
    isOpen: false,
    limitInfo: null
  });
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [competitorBrands, setCompetitorBrands] = useState([]);
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);
  const [pipelineStatuses, setPipelineStatuses] = useState({});
  const [addingToPipeline, setAddingToPipeline] = useState({});
  const [userBrands, setUserBrands] = useState([]);
  const [brandPopup, setBrandPopup] = useState({ visible: false, affiliateUrl: '', email: '', x: 0, y: 0 });

  const analysisSteps = [
    'Validating competitor URL...',
    'Checking brand in database...',
    'Verifying gap analysis limits...',
    'Fetching competitor backlinks...',
    'Matching with affiliate database...',
    'Finding affiliate gaps...',
    'Analyzing traffic patterns...',
    'Performing gap analysis...',
    'Finalizing gap results...'
  ];

  // Toast notification functions
  const showNotification = (message, type = 'info') => {
    setToast({
      isVisible: true,
      message,
      type
    });

    setTimeout(() => {
      setToast(prev => ({ ...prev, isVisible: false }));
    }, 5000);
  };

  const hideNotification = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  const handleUpgrade = useCallback(() => {
    window.location.href = '/pricing-plan';
  }, []);

  // Fetch user brands for pipeline brand selection
  useEffect(() => {
    const fetchUserBrands = async () => {
      try {
        const response = await fetch(`${basePath}/api/crm/brands`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        const result = await response.json();
        if (result.success && result.data) {
          setUserBrands(result.data);
        }
      } catch (error) {
        console.error('Error fetching brands:', error);
      }
    };
    fetchUserBrands();
  }, []);

  // Check CRM pipeline status when affiliate data changes
  useEffect(() => {
    const checkPipelineStatuses = async () => {
      if (!affiliateData || affiliateData.length === 0) return;
      const urls = affiliateData.map(item => item.url).filter(Boolean);
      if (urls.length === 0) return;
      try {
        const response = await fetch(`${basePath}/api/crm/check-status`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ affiliate_urls: urls })
        });
        const result = await response.json();
        if (result.success) {
          setPipelineStatuses(result.data);
        }
      } catch (error) {
        console.error('Error checking pipeline statuses:', error);
      }
    };
    checkPipelineStatuses();
  }, [affiliateData]);

  // Show brand selection popup when clicking Prospect
  const handleProspectClick = (e, affiliateUrl, email) => {
    e.stopPropagation();
    e.preventDefault();
    setBrandPopup({
      visible: true,
      affiliateUrl,
      email
    });
  };

  // Close brand popup on outside click
  useEffect(() => {
    if (!brandPopup.visible) return;
    const handleClickOutside = (e) => {
      const popupEl = document.getElementById('gap-brand-pipeline-popup');
      if (popupEl && popupEl.contains(e.target)) return;
      setBrandPopup(prev => ({ ...prev, visible: false }));
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 10);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [brandPopup.visible]);

  // Add affiliate to CRM pipeline with brand_id
  const handleAddToPipeline = async (affiliateUrl, email, brandId) => {
    setBrandPopup(prev => ({ ...prev, visible: false }));
    setAddingToPipeline(prev => ({ ...prev, [affiliateUrl]: true }));
    try {
      const response = await fetch(`${basePath}/api/crm/add`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          affiliate_url: affiliateUrl,
          email: email || null,
          source: 'affiliate-gap',
          brand_id: brandId
        })
      });
      const result = await response.json();
      if (result.success || result.data) {
        const newEntry = {
          stage: result.data?.stage || 'prospected',
          id: result.data?.id,
          brand_id: result.data?.brand_id || brandId,
          brand_name: result.data?.brand_name
        };
        setPipelineStatuses(prev => {
          const existing = prev[affiliateUrl] || [];
          const alreadyExists = existing.some(e => e.brand_id === newEntry.brand_id);
          return {
            ...prev,
            [affiliateUrl]: alreadyExists ? existing : [...existing, newEntry]
          };
        });
        showNotification('Added to CRM pipeline', 'success');
      } else {
        showNotification(result.message || 'Already in pipeline', 'info');
      }
    } catch (error) {
      console.error('Error adding to pipeline:', error);
      showNotification('Failed to add to pipeline', 'error');
    } finally {
      setAddingToPipeline(prev => ({ ...prev, [affiliateUrl]: false }));
    }
  };

  // Email popup functions - with contact finder fallback
  const openEmailPopup = async (affiliate) => {
    const affiliateUrl = affiliate.url;

    // If email already exists, open popup directly
    if (affiliate.email && affiliate.email.trim() !== '') {
      const emails = affiliate.email.split(',').map(e => e.trim()).filter(Boolean);
      const contact = {
        email: emails[0],
        name: affiliate.url,
        url: affiliate.url,
        affiliateData: affiliate
      };
      setEmailPopup({ isOpen: true, contact, allEmails: emails, socialMedia: {} });
      return;
    }

    // No email — run contact finder
    setFindingContact(prev => ({ ...prev, [affiliateUrl]: true }));
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
      const response = await fetch(`${apiUrl}/api/affiliate-recruitment/find-hunter-contact`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ affiliateUrl })
      });

      const result = await response.json();

      if (result.success && result.contacts && result.contacts.length > 0) {
        const allEmails = result.contacts.map(c => c.value).filter(Boolean);
        const contact = {
          email: allEmails[0],
          name: affiliate.url,
          url: affiliate.url,
          affiliateData: { ...affiliate, email: allEmails.join(', ') }
        };
        setEmailPopup({ isOpen: true, contact, allEmails, socialMedia: result.socialMedia || {} });
        showNotification(`Found ${result.contacts.length} contact(s) for ${affiliateUrl}`, 'success');
      } else {
        const contact = {
          email: `contact@${affiliate.url}`,
          name: affiliate.url,
          url: affiliate.url,
          affiliateData: affiliate
        };
        setEmailPopup({ isOpen: true, contact, allEmails: [], socialMedia: result?.socialMedia || {} });
        showNotification('No contacts found — using default email', 'info');
      }
    } catch (error) {
      console.error('Error finding contacts:', error);
      const contact = {
        email: `contact@${affiliate.url}`,
        name: affiliate.url,
        url: affiliate.url,
        affiliateData: affiliate
      };
      setEmailPopup({ isOpen: true, contact, allEmails: [], socialMedia: {} });
    } finally {
      setFindingContact(prev => ({ ...prev, [affiliateUrl]: false }));
    }
  };

  const closeEmailPopup = () => {
    setEmailPopup({ isOpen: false, contact: null, allEmails: [], socialMedia: {} });
  };

  const handleEmailSend = async (emailData, recipient) => {
    try {
      
      const apiUrl = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
      
      if (!emailData.to || !emailData.subject || !emailData.message) {
        showNotification('Please fill in all required fields (To, Subject, Message)', 'error');
        return;
      }
      
      const payload = {
        to: emailData.to.trim(),
        cc: emailData.cc ? emailData.cc.trim() : '',
        subject: emailData.subject.trim(),
        message: emailData.message.trim(),
        attachments: emailData.attachments || [],
        sender_email_id: emailData.sender_email_id || 'primary'
      };

      const response = await fetch(`${apiUrl}/api/emailsent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const responseText = await response.text();
      let result;
      
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', responseText);
        throw new Error(`Server returned invalid JSON: ${responseText}`);
      }

      if (!response.ok) {
        throw new Error(result.error || result.message || `HTTP error! status: ${response.status}`);
      }

      if (result.success) {
        showNotification('Email sent successfully!', 'success');
        closeEmailPopup();
      } else {
        throw new Error(result.error || result.message || 'Failed to send email');
      }
      
    } catch (error) {
      console.error('Error sending email:', error);
      showNotification(error.message || 'Failed to send email', 'error');
    }
  };

  // Load brands on component mount
  useEffect(() => {
    const loadInitialBrands = async () => {
      try {
        const brandData = await fetchBrandData();
        
        if (Array.isArray(brandData)) {
          setBrands(brandData);
        } else if (brandData && Array.isArray(brandData.data)) {
          setBrands(brandData.data);
        } else if (brandData && typeof brandData === 'object') {
          const possibleArrays = Object.values(brandData).filter(value => Array.isArray(value));
          if (possibleArrays.length > 0) {
            setBrands(possibleArrays[0]);
          } else {
            setBrands([]);
          }
        } else {
          setBrands([]);
        }
        
      } catch (error) {
        console.error('Error loading brands:', error);
        setBrands([]);
      }
    };

    loadInitialBrands();
  }, []);

  // Function to load competitor brands (can be reused)
  const loadCompetitorBrands = useCallback(async () => {
    try {
      setIsLoadingBrands(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

      const response = await fetch(`${apiUrl}/api/gap/competitor-brands`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && Array.isArray(result.data)) {
        setCompetitorBrands(result.data);
      } else {
        setCompetitorBrands([]);
      }
    } catch (error) {
      console.error('Error loading competitor brands:', error);
      setCompetitorBrands([]);
    } finally {
      setIsLoadingBrands(false);
    }
  }, []);

  // Load competitor brands on component mount
  useEffect(() => {
    loadCompetitorBrands();
  }, [loadCompetitorBrands]);

  // Form validation
  const validateForm = async () => {
    const newErrors = {};
    let isValid = true;

    if (!formData.brandName.trim()) {
      newErrors.brandName = 'Brand selection is required';
      isValid = false;
    }

    if (!formData.spyUrl.trim()) {
      newErrors.spyUrl = 'Website URL is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (field, value) => {
    // Block HTML/script injection
    if (/<[^>]*>/g.test(value)) return;
    // Brand name: only letters, numbers, spaces, dots, hyphens, ampersands
    if (field === 'brandName' && value && !/^[a-zA-Z0-9\s.\-&]*$/.test(value)) return;
    // URL: block spaces and invalid chars
    if (field === 'spyUrl' && value && /[\s<>"'{}|\\^`\[\]]/.test(value)) return;

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Reset verification when URL changes
    if (field === 'spyUrl') {
      setUrlVerified(false);
      setVerificationMessage('');
    }

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Auto-verify URL when user stops typing a valid domain
  useEffect(() => {
    const trimmedUrl = formData.spyUrl?.trim() || '';

    if (!trimmedUrl) {
      setUrlVerified(false);
      setVerificationMessage('');
      return;
    }

    const domainPattern = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
    const cleanUrl = trimmedUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
    if (!domainPattern.test(cleanUrl)) return;

    if (verifyTimerRef.current) clearTimeout(verifyTimerRef.current);

    verifyTimerRef.current = setTimeout(async () => {
      setVerifyingUrl(true);
      setVerificationMessage('');

      const normalizedUrl = trimmedUrl.startsWith('http') ? trimmedUrl : 'https://' + trimmedUrl;
      const API_BASE = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

      try {
        const response = await fetch(`${API_BASE}/api/brandAddition/verify-url`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: normalizedUrl })
        });

        const data = await response.json();

        if (data.success && data.isGamblingBrand) {
          setUrlVerified(true);
          setVerificationMessage(`Verified! Detected: ${data.verticals?.join(', ') || 'Gambling Brand'}`);
        } else {
          setUrlVerified(false);
          setVerificationMessage(data.message || 'This URL does not appear to be a gambling/betting website.');
        }
      } catch (err) {
        setUrlVerified(false);
        setVerificationMessage('Failed to verify URL. Please try again.');
      } finally {
        setVerifyingUrl(false);
      }
    }, 800);

    return () => {
      if (verifyTimerRef.current) clearTimeout(verifyTimerRef.current);
    };
  }, [formData.spyUrl]);

  const startAnalysis = async () => {
    // Store progressInterval ref for cleanup - declared outside try block for catch access
    let progressIntervalRef = null;
    let maxTimeout = null;

    try {
      const isFormValid = await validateForm();
      if (!isFormValid) {
        return;
      }

      if (!urlVerified) {
        setErrors(prev => ({ ...prev, spyUrl: 'Please verify the URL before starting analysis' }));
        return;
      }

      setIsAnalyzing(true);
      setProgress(0);
      setCurrentStep(analysisSteps[0]);
      setShowResults(false);

      // 30-second timeout to auto-close popup
      maxTimeout = setTimeout(() => {
        if (progressIntervalRef) {
          clearInterval(progressIntervalRef);
        }
        setIsAnalyzing(false);
        setProgress(0);
        setCurrentStep('');
        // Reload competitor brands to check if brand was added
        loadCompetitorBrands();
      }, 30000);

      progressIntervalRef = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + 8;
          const stepIndex = Math.floor((newProgress / 100) * analysisSteps.length);
          if (stepIndex < analysisSteps.length) {
            setCurrentStep(analysisSteps[stepIndex]);
          }
          if (newProgress >= 80) {
            clearInterval(progressIntervalRef);
            return 80;
          }
          return newProgress;
        });
      }, 200);

      const gapResult = await fetchGapAnalysis(formData.brandName, formData.spyUrl);

      // Clear the max timeout since we got a response
      clearTimeout(maxTimeout);

      // Handle limit exceeded case
      if (!gapResult.success && gapResult.isLimitExceeded) {
        clearInterval(progressIntervalRef);
        clearTimeout(maxTimeout);
        setIsAnalyzing(false);
        setLimitExceededModal({
          isOpen: true,
          limitInfo: gapResult.limitInfo
        });
        return;
      }

      if (gapResult.success) {
        // Check if brand is still processing in background
        if (gapResult.processing === true) {

          clearInterval(progressIntervalRef);
          clearTimeout(maxTimeout);

          // Start Redux polling for the new brand
          if (gapResult.brandId && gapResult.brandUrl) {
            dispatch(startGapPollingWithCleanup({
              brandUrl: gapResult.brandUrl,
              brandName: gapResult.brandName || gapResult.brandUrl,
              brandId: gapResult.brandId
            }));
          }

          // Close the popup and reload competitor brands
          setIsAnalyzing(false);
          setProgress(0);
          setCurrentStep('');

          // Show success notification
          showNotification(gapResult.message || 'Gap brand added successfully. Processing in background.', 'success');

          // Reload competitor brands to show the new card with processing status
          loadCompetitorBrands();

          return;
        }

        setProgress(85);
        setCurrentStep('Performing additional analysis...');

        clearInterval(progressIntervalRef);
        setProgress(100);
        setCurrentStep('Analysis complete!');
        setAffiliateData(gapResult.affiliates || []);

        // Reload competitor brands to show the newly added brand card
        loadCompetitorBrands();

        setTimeout(() => {
          clearTimeout(maxTimeout);
          setIsAnalyzing(false);
          setShowResults(true);
        }, 1000);
      } else {
        clearInterval(progressIntervalRef);
        clearTimeout(maxTimeout);
        setErrors(prev => ({
          ...prev,
          general: gapResult.error
        }));
        setIsAnalyzing(false);
      }

    } catch (error) {
      console.error('Analysis error:', error);
      // Clean up interval and timeout on error
      if (progressIntervalRef) {
        clearInterval(progressIntervalRef);
      }
      if (maxTimeout) {
        clearTimeout(maxTimeout);
      }
      setErrors(prev => ({
        ...prev,
        general: 'An unexpected error occurred. Please try again.'
      }));
      setIsAnalyzing(false);
    }
  };

  const handleGeoMouseEnter = (e, geoBreakdown) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top
    });
    setTooltipData(geoBreakdown);
    setTooltipVisible(true);
  };

  const handleGeoMouseLeave = () => {
    setTooltipVisible(false);
    setTooltipData(null);
  };

  const toggleDraftAffiliate = useCallback((affiliateId) => {
    setDraftAffiliates(prev => {
      const newDraft = new Set(prev);
      if (newDraft.has(affiliateId)) {
        newDraft.delete(affiliateId);
      } else {
        newDraft.add(affiliateId);
      }
      return newDraft;
    });
  }, []);

  // Calculate planLimit
  const planLimit = getPlanDataLimit(userPlan);

  // Filter data based on draft-only mode and search
  const filteredData = useMemo(() => {
    let data = affiliateData;
    
    if (showDraftOnly) {
      data = data.filter(item => draftAffiliates.has(item.id));
    }
    
    if (globalFilter) {
      data = data.filter(item => {
        const searchLower = globalFilter.toLowerCase();
        return (
          item.url?.toLowerCase().includes(searchLower) ||
          item.totalTraffic?.toString().includes(searchLower) ||
          item.organicKeywords?.toString().includes(searchLower) ||
          item.topKeywords?.some(k => k.keyword?.toLowerCase().includes(searchLower)) ||
          item.topPaidKeywords?.some(k => k.keyword?.toLowerCase().includes(searchLower))
        );
      });
    }
    
    return data;
  }, [affiliateData, showDraftOnly, draftAffiliates, globalFilter]);

  // Table columns definition
  const columns = useMemo(() => [
    {
      id: 'expander',
      header: () => null,
      cell: ({ row, getValue }) => {
        const overallIndex = currentPage * pageSize + row.index;
        const showAsImage = planLimit !== null && overallIndex >= planLimit;

        if (showAsImage) {
          return (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={handleUpgrade}
                className="text-yellow-600 hover:text-yellow-800 transition-colors"
              >
                <Crown className="w-4 h-4" />
              </button>
            </div>
          );
        }

        return (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => {
                const newSelection = new Set(rowSelection);
                if (newSelection.has(row.id)) {
                  newSelection.delete(row.id);
                } else {
                  newSelection.add(row.id);
                }
                setRowSelection(newSelection);
              }}
              className={`transition-colors duration-200 ${
                rowSelection.has(row.id)
                  ? 'text-blue-600 hover:text-blue-700'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              {rowSelection.has(row.id) ? (
                <CheckSquare className="w-4 h-4" />
              ) : (
                <Square className="w-4 h-4" />
              )}
            </button>
            <button
              onClick={() => {
                const newExpanded = new Set(expandedRows);
                if (newExpanded.has(row.id)) {
                  newExpanded.delete(row.id);
                } else {
                  newExpanded.add(row.id);
                }
                setExpandedRows(newExpanded);
              }}
              className={`text-blue-600 hover:text-blue-800 transition-all duration-200 transform hover:scale-110 ${
                expandedRows.has(row.id) ? 'rotate-180' : ''
              }`}
            >
              {expandedRows.has(row.id) ? 
                <Minus className="w-4 h-4" /> : 
                <Plus className="w-4 h-4" />
              }
            </button>
            <button
              onClick={() => toggleDraftAffiliate(row.original.id)}
              className={`transition-colors ${
                draftAffiliates.has(row.original.id)
                  ? 'text-blue-600 hover:text-blue-800'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              {draftAffiliates.has(row.original.id) ? 
                <BookmarkCheck className="w-4 h-4" /> : 
                <Bookmark className="w-4 h-4" />
              }
            </button>
          </div>
        );
      }
    },
    {
      accessorKey: 'url',
      header: 'Affiliate URL',
      cell: ({ getValue, row }) => {
        const overallIndex = currentPage * pageSize + row.index;
        const showAsImage = planLimit !== null && overallIndex >= planLimit;

        if (showAsImage) {
          return (
            <div className="text-sm font-medium text-gray-400 blur-sm">
              {getValue()}
            </div>
          );
        }

        return (
          <div className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer transition-colors text-center">
            {getValue()}
          </div>
        );
      }
    },
    {
      accessorKey: 'totalTraffic',
      header: 'Total Traffic',
      cell: ({ row }) => {
        const overallIndex = currentPage * pageSize + row.index;
        const showAsImage = planLimit !== null && overallIndex >= planLimit;

        if (showAsImage) {
          return (
            <div className="space-y-1 text-center blur-sm">
              <div className="text-sm font-medium text-gray-400">{formatNumber(row.original.totalTraffic)}</div>
              <ComparisonIndicator value={row.original.totalTrafficComparison} className="opacity-50" />
            </div>
          );
        }

        return (
          <div className="space-y-1 text-center">
            <div className="text-sm font-medium text-gray-900">{formatNumber(row.original.totalTraffic)}</div>
            <ComparisonIndicator value={row.original.totalTrafficComparison} />
          </div>
        );
      }
    },
    {
      accessorKey: 'organicKeywords',
      header: 'Organic Keywords',
      cell: ({ row }) => {
        const overallIndex = currentPage * pageSize + row.index;
        const showAsImage = planLimit !== null && overallIndex >= planLimit;

        if (showAsImage) {
          return (
            <div className="space-y-1 text-center blur-sm">
              <div className="text-sm text-gray-400">{formatNumber(row.original.organicKeywords)}</div>
              <ComparisonIndicator value={row.original.organicKeywordsComparison} className="opacity-50" />
            </div>
          );
        }

        return (
          <div className="space-y-1 text-center">
            <div className="text-sm text-gray-900">{formatNumber(row.original.organicKeywords)}</div>
            <ComparisonIndicator value={row.original.organicKeywordsComparison} />
          </div>
        );
      }
    },
    {
      accessorKey: 'organicTraffic',
      header: 'Organic Traffic',
      cell: ({ row }) => {
        const overallIndex = currentPage * pageSize + row.index;
        const showAsImage = planLimit !== null && overallIndex >= planLimit;

        if (showAsImage) {
          return (
            <div className="space-y-1 text-center blur-sm">
              <div className="text-sm font-medium text-gray-400">{formatNumber(row.original.organicTraffic)}</div>
              <ComparisonIndicator value={row.original.organicTrafficComparison} className="opacity-50" />
            </div>
          );
        }

        return (
          <div className="space-y-1 text-center">
            <div className="text-sm font-medium text-blue-600">{formatNumber(row.original.organicTraffic)}</div>
            <ComparisonIndicator value={row.original.organicTrafficComparison} />
          </div>
        );
      }
    },
    {
      accessorKey: 'paidTraffic',
      header: 'Paid Traffic',
      cell: ({ row }) => {
        const overallIndex = currentPage * pageSize + row.index;
        const showAsImage = planLimit !== null && overallIndex >= planLimit;

        if (showAsImage) {
          return (
            <div className="space-y-1 text-center blur-sm">
              <div className="text-sm font-medium text-gray-400">{formatNumber(row.original.paidTraffic)}</div>
              <ComparisonIndicator value={row.original.paidTrafficComparison} className="opacity-50" />
            </div>
          );
        }

        return (
          <div className="space-y-1 text-center">
            <div className="text-sm font-medium text-blue-600">{formatNumber(row.original.paidTraffic)}</div>
            <ComparisonIndicator value={row.original.paidTrafficComparison} />
          </div>
        );
      }
    },
    {
      accessorKey: 'topGeos',
      header: 'Top Geos',
      cell: ({ row }) => {
        const overallIndex = currentPage * pageSize + row.index;
        const showAsImage = planLimit !== null && overallIndex >= planLimit;

        if (showAsImage) {
          return (
            <div className="relative group cursor-pointer text-center blur-sm">
              <div className="inline-flex items-center gap-1 text-xs">
                {row.original.geoBreakdown?.slice(0, 2).map((geo, index) => (
                  <span key={geo.code} className="inline-flex items-center gap-1">
                    {index > 0 && <span className="text-gray-400">, </span>}
                    <span className="text-gray-400">{geo.code}</span>
                    <span className="text-gray-400 font-medium">[{formatNumber(geo.organic)}]</span>
                  </span>
                ))}
                {(row.original.geoBreakdown?.length || 0) > 2 && (
                  <span className="text-gray-400 ml-1">
                    , +{row.original.geoBreakdown.length - 2} more
                  </span>
                )}
              </div>
            </div>
          );
        }

        return (
          <div 
            className="relative group cursor-pointer text-center"
            onMouseEnter={(e) => handleGeoMouseEnter(e, row.original.geoBreakdown)}
            onMouseLeave={handleGeoMouseLeave}
          >
            <div className="inline-flex items-center gap-1 text-xs">
              {row.original.geoBreakdown?.slice(0, 2).map((geo, index) => (
                <span key={geo.code} className="inline-flex items-center gap-1">
                  {index > 0 && <span className="text-gray-400">, </span>}
                  <span className="text-gray-700">{geo.code}</span>
                  <span className="text-blue-600 font-medium">[{formatNumber(geo.organic)}]</span>
                </span>
              ))}
              {(row.original.geoBreakdown?.length || 0) > 2 && (
                <span className="text-gray-500 ml-1 underline decoration-dotted">
                  , +{row.original.geoBreakdown.length - 2} more
                </span>
              )}
            </div>
          </div>
        );
      }
    },
    {
      id: 'contact',
      header: 'Contact',
      cell: ({ row }) => {
        const overallIndex = currentPage * pageSize + row.index;
        const showAsImage = planLimit !== null && overallIndex >= planLimit;

        if (showAsImage) {
          return (
            <div className="text-center">
              <button 
                onClick={handleUpgrade}
                className="bg-gray-400 text-white text-xs font-medium py-1 px-3 rounded-lg blur-sm mx-auto cursor-not-allowed"
                disabled
              >
                <Mail className="w-3 h-3 inline mr-1" />
                Contact
              </button>
            </div>
          );
        }

        const isSearching = findingContact[row.original.url];

        return (
          <div className="text-center">
            <button
              onClick={() => !isSearching && openEmailPopup(row.original)}
              disabled={isSearching}
              className={`${isSearching ? 'bg-yellow-500' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'} text-white text-xs font-medium py-1 px-3 rounded-lg transition-all duration-300 flex items-center gap-1 transform hover:scale-105 hover:shadow-lg shadow-blue-600/30 mx-auto`}
            >
              {isSearching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
              {isSearching ? 'Finding...' : 'Contact'}
            </button>
          </div>
        );
      }
    },
    {
      id: 'pipeline',
      header: 'Pipeline',
      cell: ({ row }) => {
        const overallIndex = currentPage * pageSize + row.index;
        const showAsImage = planLimit !== null && overallIndex >= planLimit;
        if (showAsImage) return null;

        const affiliateUrl = row.original.url;
        const pipelineEntries = pipelineStatuses[affiliateUrl]; // Now an array

        const stageColors = {
          prospected: 'bg-blue-100 text-blue-700',
          contacted: 'bg-purple-100 text-purple-700',
          negotiating: 'bg-amber-100 text-amber-700',
          onboarding: 'bg-cyan-100 text-cyan-700',
          active: 'bg-green-100 text-green-700',
          churned: 'bg-red-100 text-red-700'
        };

        return (
          <div className="flex flex-col items-center gap-1">
            {/* Show existing brand pipeline statuses */}
            {pipelineEntries && pipelineEntries.length > 0 && (
              <div className="flex flex-col gap-1">
                {pipelineEntries.map((entry, idx) => {
                  const colorClass = stageColors[entry.stage] || 'bg-gray-100 text-gray-700';
                  return (
                    <span key={idx} className={`inline-flex items-center text-[9px] font-semibold px-2 py-0.5 rounded-full capitalize ${colorClass}`} title={`${entry.brand_name}: ${entry.stage}`}>
                      {entry.brand_name}: {entry.stage}
                    </span>
                  );
                })}
              </div>
            )}
            {/* Always show Prospect button to add for more brands */}
            <button
              onClick={(e) => handleProspectClick(e, affiliateUrl, row.original.email)}
              disabled={addingToPipeline[affiliateUrl]}
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 transform hover:scale-105 hover:shadow-lg shadow-green-500/30 text-white text-[10px] font-medium py-0.5 px-2 rounded-lg transition-all duration-300 flex items-center gap-1 mx-auto"
            >
              {addingToPipeline[affiliateUrl] ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-3 h-3" />
                  Prospect
                </>
              )}
            </button>
          </div>
        );
      }
    }
  ], [draftAffiliates, toggleDraftAffiliate, planLimit, userPlan, handleUpgrade, currentPage, pageSize, rowSelection, expandedRows, pipelineStatuses, addingToPipeline, handleAddToPipeline, handleProspectClick, findingContact]);

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const startIndex = currentPage * pageSize;
  const endIndex = startIndex + pageSize;
  const currentData = filteredData.slice(startIndex, endIndex);

  // Render the blurred row
  const renderBlurredRow = (row) => {
    return (
      <tr className="group hover:bg-gray-50 transition-all duration-200 relative">
        <td colSpan={columns.length} className="p-0 relative">
          {/* Blurred image row */}
          <img 
            src="./blurrow.png"
            alt={`Preview of ${row.url || 'affiliate'}`}
            className="w-full object-cover"
            onError={(e) => { 
              // Fallback to blurred content if image fails to load
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          
          {/* Fallback blurred content (hidden by default) */}
          <div className="items-center justify-between px-6 py-4 blur-sm opacity-60 pointer-events-none" style={{ display: 'none' }}>
            <div className="flex items-center gap-4">
              <div className="w-6 h-6 bg-gray-300 rounded"></div>
              <div className="space-y-1">
                <div className="h-4 bg-gray-300 rounded w-32"></div>
                <div className="h-3 bg-gray-200 rounded w-24"></div>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="h-4 bg-gray-300 rounded w-16"></div>
              <div className="h-4 bg-gray-300 rounded w-16"></div>
              <div className="h-4 bg-gray-300 rounded w-16"></div>
              <div className="h-4 bg-gray-300 rounded w-20"></div>
              <div className="h-6 bg-gray-300 rounded w-16"></div>
            </div>
          </div>
          
          {/* Hover Overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-75 transition-all duration-300 flex items-center justify-center">
            <button
              onClick={handleUpgrade}
              className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-90 group-hover:scale-100 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium px-6 py-3 rounded-lg shadow-lg hover:shadow-xl flex items-center gap-2"
            >
              <Crown className="w-5 h-5" />
              <span>Upgrade to View Data</span>
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="p-4 min-h-screen bg-gray-100 dark:bg-slate-950">
      <GeoTooltip
        geoBreakdown={tooltipData}
        isVisible={tooltipVisible}
        position={tooltipPosition}
      />

      {isAnalyzing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 rounded-full flex items-center justify-center mb-6 animate-pulse">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>

              <h3 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mb-2">
                Finding Affiliate Gap Opportunities
              </h3>
              <p className="text-gray-600 dark:text-slate-400 mb-6">
                Analyzing affiliates working with competitor but not with your brand...
              </p>

              <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-3 mb-4 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-300 ease-out shadow-lg bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-500 dark:to-blue-600"
                  style={{ width: `${progress}%` }}
                />
              </div>

              <p className="text-sm font-medium text-blue-700 dark:text-blue-400">
                {currentStep}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
          <h1 className="text-2xl font-semibold mb-2 text-gray-800 dark:text-slate-100 flex items-center gap-3">
            <Eye className="w-6 h-6 text-blue-600 dark:text-blue-500" />
            Affiliate Gap Analysis
          </h1>
          <p className="text-gray-600 dark:text-slate-400">
            Discover affiliates working with competitors but not with your brand
          </p>
        </div>
      </div>

      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
          <h2 className="text-lg font-medium text-gray-800 dark:text-slate-100 mb-4">Affiliate Gap Finder Setup</h2>

          {errors.general && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg text-red-700 dark:text-red-300">
              {errors.general}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Select Your Brand *
              </label>
              <div className="relative">
                <select
                  value={formData.brandName}
                  onChange={(e) => handleInputChange('brandName', e.target.value)}
                  disabled={isAnalyzing}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 appearance-none pr-10 dark:bg-slate-800 dark:text-slate-100 ${
                    errors.brandName ? 'border-red-300 dark:border-red-700' : 'border-gray-200 dark:border-slate-700'
                  }`}
                >
                  <option value="">Select a brand...</option>
                  {Array.isArray(brands) && brands.length > 0 ? (
                    brands.map((brand, index) => (
                      <option key={index} value={brand.brand_url || brand.name || brand.brandName || brand}>
                        {brand.name || brand.brand_name || brand.brandName || brand}
                      </option>
                    ))
                  ) : (
                    <option disabled>Loading brands...</option>
                  )}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-slate-500 pointer-events-none" />
              </div>
              {errors.brandName && (
                <p className="mt-1 text-red-600 dark:text-red-400 text-sm">{errors.brandName}</p>
              )}
            </div>

            <div className="relative pb-10">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Competitor Website URL *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.spyUrl}
                  onChange={(e) => handleInputChange('spyUrl', e.target.value)}
                  placeholder="e.g., betwin-casino.com"
                  disabled={isAnalyzing}
                  className={`w-full px-4 py-3 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-slate-800 dark:text-slate-100 ${
                    errors.spyUrl ? 'border-red-300 dark:border-red-700' : urlVerified ? 'border-green-400 dark:border-green-600' : 'border-gray-200 dark:border-slate-700'
                  }`}
                />
                {verifyingUrl && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                  </div>
                )}
                {!verifyingUrl && urlVerified && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  </div>
                )}
              </div>
              {errors.spyUrl && (
                <p className="absolute left-0 mt-1 text-red-600 dark:text-red-400 text-sm">{errors.spyUrl}</p>
              )}
              {verificationMessage && !errors.spyUrl && (
                <p className={`absolute left-0 mt-1 text-sm flex items-center gap-1 ${urlVerified ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {verificationMessage}
                </p>
              )}
              {!verificationMessage && !errors.spyUrl && (
                <p className="absolute left-0 bottom-0 text-xs text-gray-500 dark:text-slate-400">
                  Enter without http:// or www. (e.g., example.com)
                </p>
              )}
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={startAnalysis}
              disabled={isAnalyzing}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300 flex items-center gap-2"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Analyzing...</span>
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  <span>Find Affiliate Gaps</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Competitor Brands Section */}
      <div className="mb-6">
        {isLoadingBrands ? (
          <div className="flex justify-center items-center py-12 bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-500" />
            <span className="ml-2 text-gray-600 dark:text-slate-400">Loading competitor brands...</span>
          </div>
        ) : competitorBrands.length > 0 ? (
          <div className="space-y-6">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-800 dark:text-slate-100">
                Your Competitor Brands ({competitorBrands.length})
              </h3>
              <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
                Brands you've analyzed for affiliate gap opportunities
              </p>
            </div>
            {competitorBrands.map((brand) => (
              <GapCard
                key={brand.id || brand.brand_url}
                brand={brand}
                onStatusUpdate={(brandId, status) => {
                  // Reload competitor brands when processing completes
                  if (status === 'completed') {
                    loadCompetitorBrands();
                  }
                }}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-8 text-center">
            <Eye className="w-12 h-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-800 dark:text-slate-100 mb-2">No Competitor Brands Yet</h3>
            <p className="text-gray-600 dark:text-slate-400">
              Start by analyzing your first competitor to find affiliate gaps.
            </p>
          </div>
        )}
      </div>

      {showResults && affiliateData.length > 0 && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-200 dark:border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div>
                  <h2 className="text-lg font-medium text-gray-800 dark:text-slate-100">Search Results</h2>
                  <p className="text-sm text-gray-600 dark:text-slate-400">
                    <PlanStatusDisplay userPlan={userPlan} totalResults={filteredData.length} />
                    {rowSelection.size > 0 && (
                      <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                        {rowSelection.size} selected
                      </span>
                    )}
                  </p>
                </div>

                <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg border border-blue-200 dark:border-blue-700 shadow-sm">
                  <Bookmark className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                    Draft: {draftAffiliates.size}
                  </span>
                  {draftAffiliates.size > 0 && (
                    <button
                      onClick={() => setShowDraftOnly(!showDraftOnly)}
                      className={`ml-2 px-3 py-1 text-xs rounded-full transition-all duration-300 transform hover:scale-105 ${
                        showDraftOnly
                          ? 'bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 text-white shadow-lg shadow-blue-600/30'
                          : 'bg-blue-200 dark:bg-blue-800 text-blue-700 dark:text-blue-300 hover:bg-blue-300 dark:hover:bg-blue-700 hover:shadow-md'
                      }`}
                    >
                      {showDraftOnly ? 'Show All' : 'Draft Only'}
                    </button>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" />
                  <input
                    type="text"
                    placeholder="Search by URL, keywords..."
                    value={globalFilter ?? ''}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    className="pl-10 pr-12 py-2 border border-gray-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 text-sm w-64 transition-all duration-200 hover:shadow-md focus:shadow-md dark:bg-slate-800 dark:text-slate-100"
                  />
                </div>

                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(0);
                  }}
                  className="border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-all duration-200 hover:shadow-md dark:bg-slate-800 dark:text-slate-100"
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-slate-800 dark:to-slate-900 border-b border-gray-200 dark:border-slate-700">
                <tr>
                  <th className="px-6 py-4 text-center whitespace-nowrap"></th>
                  {columnVisibility.url && (
                    <th className="px-6 py-4 text-center whitespace-nowrap">
                      <button className="flex items-center justify-center gap-2 text-xs font-medium text-blue-600 dark:text-blue-400 tracking-wider hover:text-blue-700 dark:hover:text-blue-300 transition-colors w-full">
                        <Globe className="w-4 h-4" />
                        Affiliate URL
                      </button>
                    </th>
                  )}
                  {columnVisibility.totalTraffic && (
                    <th className="px-6 py-4 text-center whitespace-nowrap">
                      <button className="flex items-center justify-center gap-2 text-xs font-medium text-blue-600 tracking-wider hover:text-blue-700 transition-colors w-full">
                        <TrendingUp className="w-4 h-4" />
                        Total Traffic
                      </button>
                    </th>
                  )}
                  {columnVisibility.organicKeywords && (
                    <th className="px-6 py-4 text-center whitespace-nowrap">
                      <button className="flex items-center justify-center gap-2 text-xs font-medium text-blue-600 tracking-wider hover:text-blue-700 transition-colors w-full">
                        <Hash className="w-4 h-4" />
                        Organic Keywords
                      </button>
                    </th>
                  )}
                  {columnVisibility.organicTraffic && (
                    <th className="px-6 py-4 text-center whitespace-nowrap">
                      <button className="flex items-center justify-center gap-2 text-xs font-medium text-blue-600 tracking-wider hover:text-blue-700 transition-colors w-full">
                        <TrendingUp className="w-4 h-4" />
                        Organic Traffic
                      </button>
                    </th>
                  )}
                  {columnVisibility.paidTraffic && (
                    <th className="px-6 py-4 text-center whitespace-nowrap">
                      <button className="flex items-center justify-center gap-2 text-xs font-medium text-blue-600 tracking-wider hover:text-blue-700 transition-colors w-full">
                        <MousePointer className="w-4 h-4" />
                        Paid Traffic
                      </button>
                    </th>
                  )}
                  {columnVisibility.topGeos && (
                    <th className="px-6 py-4 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2 text-xs font-medium text-blue-600 tracking-wider">
                        <Flag className="w-4 h-4" />
                        Top Geos
                      </div>
                    </th>
                  )}
                  {columnVisibility.contact && (
                    <th className="px-6 py-4 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2 text-xs font-medium text-blue-600 tracking-wider">
                        <Mail className="w-4 h-4" />
                        Contact
                      </div>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-slate-700">
                {currentData.map((row, rowIndex) => {
                  const overallIndex = currentPage * pageSize + rowIndex;
                  const showAsImage = planLimit !== null && overallIndex >= planLimit;

                  if (showAsImage) {
                    return renderBlurredRow(row);
                  }

                  return (
                    <React.Fragment key={row.id}>
                      <tr className={`hover:bg-gray-50 dark:hover:bg-slate-800 transition-all duration-200 ${
                        expandedRows.has(row.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                      } ${rowSelection.has(row.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                        {columns.map((column, colIndex) => {
                          const cellData = column.cell({
                            row: { 
                              id: row.id, 
                              index: rowIndex, 
                              original: row,
                              getValue: () => row[column.accessorKey]
                            },
                            getValue: () => row[column.accessorKey]
                          });

                          if (column.id === 'expander' || 
                              (column.accessorKey && columnVisibility[column.accessorKey]) ||
                              (column.id && columnVisibility[column.id])) {
                            return (
                              <td key={`${row.id}-${colIndex}`} className="px-6 py-4 whitespace-nowrap">
                                {cellData}
                              </td>
                            );
                          }
                          return null;
                        })}
                      </tr>
                      {expandedRows.has(row.id) && (
                        <tr className="animate-in slide-in-from-top-4 duration-300">
                          <ExpandedRowContent row={{ original: row }} pipelineStatuses={pipelineStatuses} />
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {currentData.length === 0 && (
            <div className="px-6 py-8 text-center">
              <p className="text-gray-500 dark:text-slate-400">No affiliates found.</p>
            </div>
          )}

          {currentData.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700 dark:text-slate-300">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredData.length)} of {filteredData.length} results
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                    className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      currentPage === 0
                        ? 'text-gray-400 dark:text-slate-500 cursor-not-allowed'
                        : 'text-gray-700 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700 hover:shadow-sm transform hover:scale-105'
                    }`}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </button>

                  <div className="flex items-center gap-1">
                    <span className="text-sm text-gray-700 dark:text-slate-300">
                      Page {currentPage + 1} of {totalPages}
                    </span>
                  </div>

                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                    disabled={currentPage === totalPages - 1}
                    className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      currentPage === totalPages - 1
                        ? 'text-gray-400 dark:text-slate-500 cursor-not-allowed'
                        : 'text-gray-700 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700 hover:shadow-sm transform hover:scale-105'
                    }`}
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Brand Selection Popup for Pipeline - Centered Modal */}
      {brandPopup.visible && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={() => setBrandPopup(prev => ({ ...prev, visible: false }))}>
          <div className="absolute inset-0 bg-black/30" />
          <div
            id="gap-brand-pipeline-popup"
            className="relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-600 p-5 w-full max-w-sm mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Select Brand for Prospecting</p>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5 truncate max-w-[250px]">{brandPopup.affiliateUrl}</p>
              </div>
              <button onClick={() => setBrandPopup(prev => ({ ...prev, visible: false }))} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
              {userBrands.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center">No brands found. Add a brand first.</p>
              ) : (
                userBrands.map(brand => {
                  const existingEntries = pipelineStatuses[brandPopup.affiliateUrl] || [];
                  const alreadyAdded = existingEntries.some(e => e.brand_id === brand.id);
                  return (
                    <button
                      key={brand.id}
                      onClick={() => !alreadyAdded && handleAddToPipeline(brandPopup.affiliateUrl, brandPopup.email, brand.id)}
                      disabled={alreadyAdded}
                      className={`w-full text-left px-4 py-3 text-sm rounded-lg transition-all flex items-center justify-between border ${
                        alreadyAdded
                          ? 'bg-gray-50 dark:bg-slate-700/50 text-gray-400 dark:text-slate-500 cursor-not-allowed border-gray-100 dark:border-slate-700'
                          : 'hover:bg-green-50 dark:hover:bg-green-900/20 text-gray-700 dark:text-gray-200 border-gray-100 dark:border-slate-700 hover:border-green-300 dark:hover:border-green-800 hover:shadow-sm'
                      }`}
                    >
                      <span className="truncate font-medium">{brand.brand_name || brand.brand_url}</span>
                      {alreadyAdded ? (
                        <span className="text-[10px] bg-gray-100 dark:bg-slate-600 text-gray-500 dark:text-slate-400 px-2 py-0.5 rounded-full ml-2 whitespace-nowrap">Already added</span>
                      ) : (
                        <Plus className="w-4 h-4 text-green-500 flex-shrink-0 ml-2" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      <EmailPopup
        isOpen={emailPopup.isOpen}
        onClose={closeEmailPopup}
        recipient={emailPopup.contact}
        allEmails={emailPopup.allEmails || []}
        socialMedia={emailPopup.socialMedia || {}}
        onSend={handleEmailSend}
        templates={gapAnalysisEmailTemplates}
        customTitle={emailPopup.contact ? `Contact ${emailPopup.contact.name}` : ''}
        showTemplateSelection={true}
        allowAttachments={true}
        allowCc={true}
        readOnlyTo={false}
      />

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideNotification}
      />
    </div>
  );
};

export default AffiliateGapAnalyzer;